import { useState, useEffect } from 'react';

export interface PromptField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'select';
  options?: { value: string; label: string }[]; // for type='select'
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
}

interface PromptModalProps {
  isOpen: boolean;
  title: string;
  message?: string;
  fields: PromptField[];
  submitLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onSubmit: (values: Record<string, string>) => void;
  onCancel: () => void;
}

/**
 * Configurable input dialog — replaces native browser prompt().
 * Supports 1+ fields of type text / textarea / date / select.
 * Dark-mode aware.
 */
export function PromptModal({
  isOpen,
  title,
  message,
  fields,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  danger = false,
  onSubmit,
  onCancel,
}: PromptModalProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Reset field values whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      const initial: Record<string, string> = {};
      for (const f of fields) initial[f.name] = f.defaultValue ?? '';
      setValues(initial);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const submit = () => {
    for (const f of fields) {
      if (f.required && (!values[f.name] || values[f.name].trim() === '')) {
        setError(`${f.label} is required.`);
        return;
      }
    }
    onSubmit(values);
  };

  const setField = (name: string, v: string) => setValues((prev) => ({ ...prev, [name]: v }));

  const inputCls =
    'w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded px-3 py-2 text-sm';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onCancel}></div>

      <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h2>
        {message && <p className="text-sm text-gray-600 dark:text-slate-400 mb-4 whitespace-pre-line">{message}</p>}

        <div className="space-y-3 mb-4">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
                {f.label}{f.required && <span className="text-red-500"> *</span>}
              </label>
              {f.type === 'textarea' && (
                <textarea
                  value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  rows={3}
                  className={inputCls}
                />
              )}
              {f.type === 'select' && (
                <select value={values[f.name] ?? ''} onChange={(e) => setField(f.name, e.target.value)} className={inputCls}>
                  <option value="">— Select —</option>
                  {(f.options || []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              {(f.type === 'text' || f.type === 'date') && (
                <input
                  type={f.type}
                  value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  className={inputCls}
                />
              )}
            </div>
          ))}
        </div>

        {error && <div className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</div>}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-5 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={submit}
            className={`px-5 py-2 text-white rounded-lg transition-colors ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
